import { Loader } from '../../Widgets';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import ArthaGateway from 'service/ArthaGateway';
import { Account, Receipt, Sale } from 'service';
import toast, { Toaster } from 'react-hot-toast';

export const Invoice = () => {
    const appData = useSelector((state: RootState) => state.appdata);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [sale, setSale] = useState<Sale>();
    const [showChangeStatus, setShowChangeStatus] = useState<boolean>(false);
    const [showReceiptScreen, setShowReceiptScreen] = useState<boolean>(false);
    const [receipt, setReceipt] = useState<Receipt>(new Receipt());
    const [newStatus, setNewStatus] = useState<number>(2);
    const [receiptAmount, setReceiptAmount] = useState<number>(0);
    const [deliveredTo, setDeliveredTo] = useState<string>("");
    const [accounts, setAccounts] = useState<Account[]>([]);
    const paymentTypes = ["Cash", "UPI", "Cheque", "NEFT", "RTGS", "IMPS"];
    const { id } = useParams()

    const navigate = useNavigate();

    const loadSale = () => {
        if (!id) {
            return;
        }

        ArthaGateway
            .getSale(parseInt(id))
            .then((data) => {
                setSale(data);
                if (data.status == 1) {
                    setNewStatus(2);
                } else if (data.status == 2) {
                    setNewStatus(3);
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    useEffect(() => {
        if (id) {
            loadSale();
        }
    }, [id]);

    const loadBankAcounts = () => {
        let cache_key = "bank_accounts_" + appData.companyId;
        if (sessionStorage.getItem(cache_key) !== null) {
            let localData = sessionStorage.getItem(cache_key);
            if (localData) {
                let accounts = JSON.parse(localData);
                setAccounts(accounts);
            }
        }
        else {
            ArthaGateway
                .getAccounts("", 2)
                .then((data) => {
                    setAccounts(data.entries);
                    sessionStorage.setItem(cache_key, JSON.stringify(data.entries));
                })
                .catch(error => {
                    console.log(error);
                    setIsLoading(false);
                });
        }
    }

    const printInvoice = () => {
        let url = process.env.REACT_APP_SERVER_URL + "/voucher/sale/print/" + sale?.id;
        let companyCode = localStorage.getItem("Company-code");
        if (companyCode) {
            url += "?print=1&companyCode=" + companyCode;
        }
        window.open(url, "_blank");
    }

    const changeStatus = () => {
        if (sale?.id && newStatus) {
            ArthaGateway
                .changeSaleStatus(sale?.id, newStatus, deliveredTo)
                .then((data) => {
                    toast.success("Status updated successfully");
                    setShowChangeStatus(false);
                    loadSale();
                })
                .catch(error => {
                    console.log(error);
                    toast.error("Error updating status");
                });
        }
    }

    const showNewReceipt = () => {
        if (!sale) { return; }

        let receipt = new Receipt();
        if (sale?.party) {
            receipt.party = sale.party;
            receipt.partyId = sale.party.id;
        }
        receipt.date = moment().format("YYYY-MM-DD");
        receipt.series = "2025-2026";
        receipt.transactionType = "Receipt";
        receipt.paymentType = "Cash";
        receipt.incomeAccountId = 14;
        receipt.accountId = 6;
        receipt.incomeAccount = { id: 14, name: "Sales" };
        receipt.allocations = [{ amount: sale.pendingAmount, saleId: sale.id, saleVoucherNumber: sale.voucherNumber }];
        setReceipt(receipt);

        setReceiptAmount(sale.pendingAmount);

        setShowReceiptScreen(true);
        loadBankAcounts();
    }

    const saveReceipt = () => {
        if (!sale) { return; }

        if (receiptAmount <= 0) {
            toast.error("Enter valid amount");
            return;
        }

        if (receiptAmount > sale.pendingAmount) {
            toast.error("Amount should be less than or equal to pending amount");
            return;
        }

        const foundAccount = accounts.find((account) => account.id === receipt.accountId);
        if (foundAccount) {
            receipt.account = foundAccount;
        }
        receipt.allocations = [{ amount: receiptAmount, saleId: sale.id, saleVoucherNumber: sale.voucherNumber }];
        receipt.amount = receiptAmount;
        ArthaGateway
            .saveReceipt(receipt)
            .then((data) => {
                toast('Receipt saved successfully', { position: 'bottom-center', className: 'btn bg-success' });
                setShowReceiptScreen(false);
                loadSale();
            })
            .catch(error => {
                console.log(error);
            });
    }

    const getStatusLabel = (status?: number) => {
        if (status === 1) return 'Pending';
        if (status === 2) return 'Packed';
        if (status === 3) return 'Delivered';
        return 'Unknown';
    }

    const getStatusClass = (status?: number) => {
        if (status === 1) return 'bg-danger';
        if (status === 2) return 'bg-info text-dark';
        if (status === 3) return 'bg-success';
        return 'bg-secondary';
    }

    const currencyFormat = (value: number) =>
        new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR'
        }).format(value);

    return (
        <div className='page page-new-order app-page-shell'>
            {isLoading &&
                <Loader />
            }
            {!isLoading &&
                <>
                    <div><Toaster /></div>
                    <div className='container'>
                        <div className='row justify-content-center'>
                            <div className="col-12 col-xl-10">
                                <div className='invoice-view-hero mb-3'>
                                    <div>
                                        <div className='hero-label'>Invoice Details</div>
                                        <h2>Sale #{sale?.voucherNumber}</h2>
                                        <p>{moment(sale?.date).format("DD MMM YYYY")} | {sale?.party?.name}</p>
                                    </div>
                                    <div className='hero-actions'>
                                        <button type='button' className='btn btn-outline-light btn-sm' onClick={() => navigate("/invoices")}>Back to Invoices</button>
                                    </div>
                                </div>
                                {sale?.party &&
                                    <>
                                        <div className='app-panel mb-3'>
                                            <div className='row g-2'>
                                                <div className="col-12 col-md-6">
                                                    <div className='app-soft-card invoice-view-card'>
                                                        <div className='meta-label'>Customer</div>
                                                        <div className='meta-value'>{sale.party?.name}</div>
                                                        <div className='text-muted small'>{sale.party?.mobileNumber}</div>
                                                    </div>
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <div className='app-soft-card invoice-view-card'>
                                                        <div><span className='meta-label'>Voucher</span><div className='meta-value'>{sale?.voucherNumber}</div></div>
                                                        <div className='mt-1'><span className='meta-label'>Date</span><div className='meta-value'>{moment(sale?.date).format("DD/MM/YYYY")}</div></div>
                                                        <div className='mt-1'><span className='meta-label'>Status</span><div><span className={'badge ' + getStatusClass(sale.status)}>{getStatusLabel(sale.status)}</span></div></div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {sale?.items &&
                                            <div className="app-panel mb-3">
                                                <div className='d-flex justify-content-between align-items-center mb-2'>
                                                    <div className='cart-section-title'>Items ({sale.items.length})</div>
                                                    <div className='invoice-summary-amount'>{currencyFormat(sale.billAmount)}</div>
                                                </div>
                                                <div className='cart-items'>
                                                    {sale.items.map((item, index) => (
                                                        <div className='cart-item-card' key={index}>
                                                            <div className='d-flex justify-content-between align-items-center'>
                                                                <div className='cart-item-name'>{item.product.name}</div>
                                                                <div className='cart-item-total'>{currencyFormat(item.netValue)}</div>
                                                            </div>
                                                            <div className='cart-item-unit mt-1'>{item.quantity} x {item.product.price}/{item.product.unit}</div>
                                                        </div>
                                                    ))}
                                                    {sale.items.length > 0 &&
                                                        <div className='cart-grand-total'>
                                                            <div className='cart-label'>Grand Total</div>
                                                            <h2>{currencyFormat(sale.billAmount)}</h2>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        }

                                        <div className="app-panel mb-3">
                                            <div className='d-flex justify-content-between align-items-center mb-2'>
                                                <div className='cart-section-title'>Receipts</div>
                                                {sale.pendingAmount > 0 &&
                                                    <div className='invoice-pending-chip'>Pending: {currencyFormat(sale.pendingAmount)}</div>
                                                }
                                            </div>
                                            <div className='transaction-list'>
                                                {sale.receipts && sale.receipts.length == 0 &&
                                                    <div className='app-empty-state'>
                                                        No receipts found
                                                    </div>
                                                }
                                                {sale.receipts.map((receipt, index) => (
                                                    <div className='transaction-card' key={index}>
                                                        <div className='d-flex justify-content-between align-items-start gap-2'>
                                                            <div>
                                                                <div className='transaction-title'>{moment(receipt.date).format("DD/MM/YYYY")}</div>
                                                                <div className='transaction-subtitle'>
                                                                    {receipt.paymentType} {receipt.referenceNumber ? `| ${receipt.referenceNumber}` : ''}
                                                                </div>
                                                            </div>
                                                            <div className='transaction-amount receipt'>
                                                                {currencyFormat(receipt.amount)}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {showChangeStatus &&
                                            <div className='app-panel mb-3'>
                                                <div className='app-page-header mb-2'>
                                                    <h2>Change Status</h2>
                                                </div>
                                                <div className="row g-2">
                                                    <div className='col-12 col-md-4'>
                                                        <select className="form-select" value={newStatus} onChange={(e) => setNewStatus(parseInt(e.target.value))}>
                                                            <option value="2">Packed</option>
                                                            <option value="3">Delivered</option>
                                                        </select>
                                                    </div>
                                                    {newStatus == 3 &&
                                                        <div className='col-12 col-md-4'>
                                                            <input type="text" className="form-control" placeholder="Delivered to" value={deliveredTo} onChange={(e) => setDeliveredTo(e.target.value)} />
                                                        </div>
                                                    }
                                                </div>
                                                <div className="d-flex justify-content-end gap-2 mt-3">
                                                    <button type='button' className="btn btn-sm btn-outline-dark" onClick={() => setShowChangeStatus(false)}>Cancel</button>
                                                    <button type='button' className="btn btn-sm btn-dark" onClick={() => changeStatus()}>Save Status</button>
                                                </div>
                                            </div>
                                        }

                                        {showReceiptScreen &&
                                            <div className='app-panel mb-3'>
                                                <div className='app-page-header mb-2'>
                                                    <h2>Receive Payment</h2>
                                                </div>
                                                <div className="row g-2">
                                                    <div className='col-12 col-md-4'>
                                                        <div className="form-group">
                                                            <label>Payment Type</label>
                                                            <select className="form-select" value={receipt.paymentType} onChange={(e) => setReceipt({ ...receipt, paymentType: e.target.value })}>
                                                                {paymentTypes.map((paymentType, index) => (
                                                                    <option key={index} value={paymentType}>{paymentType}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className='col-12 col-md-4'>
                                                        <div className="form-group">
                                                            <label>Account</label>
                                                            <select className="form-select" value={receipt.accountId} onChange={(e) => setReceipt({ ...receipt, accountId: parseInt(e.target.value) })}>
                                                                <option value="0">Select Account</option>
                                                                {accounts.map((account, index) => (
                                                                    <option key={index} value={account.id}>{account.name}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                    <div className='col-12 col-md-4'>
                                                        <div className="form-group">
                                                            <label>Amount</label>
                                                            <input type="number" className="form-control" placeholder="Amount" value={receiptAmount} onChange={(e) => setReceiptAmount(parseInt(e.target.value))} />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="d-flex justify-content-end gap-2 mt-3">
                                                    <button type='button' className="btn btn-sm btn-outline-dark" onClick={() => setShowReceiptScreen(false)}>Cancel</button>
                                                    <button type='button' className="btn btn-sm btn-dark" onClick={() => saveReceipt()}>Save Receipt</button>
                                                </div>
                                            </div>
                                        }

                                        <div className="app-panel">
                                            <div className="row g-2">
                                                <div className='col-12 col-md-4'>
                                                    <button type='button' className="btn btn-sm btn-dark w-100" onClick={printInvoice}>Print</button>
                                                </div>
                                                {sale?.status != 3 &&
                                                    <div className='col-12 col-md-4'>
                                                        <button type='button' className="btn btn-sm btn-outline-dark w-100" onClick={() => setShowChangeStatus(true)}>Change Status</button>
                                                    </div>
                                                }
                                                {sale?.pendingAmount > 0 &&
                                                    <div className='col-12 col-md-4'>
                                                        <button type='button' className="btn btn-sm btn-outline-dark w-100" onClick={() => showNewReceipt()}>Receive Payment</button>
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </>
            }
        </div >
    );
};